/* eslint-disable radix */
/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import Typography from '@material-ui/core/Typography';
import CancelIcon from '@material-ui/icons/Cancel';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Select from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Box from '@material-ui/core/Box';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { TimeOfDay, AutoAllocationMode } from '../../../enums';
import { autoAllocate } from '../../../actions/areas/dailyPlanning';
import RagStatusPicker from '../../common/RagStatusPicker/RagStatusPicker';
import AlertBox from '../../common/AlertBox/AlertBox';
import { useFetchClinicianGroupsOnDay } from '../../../actions/areas/clinicianGroups/fetchClinicianGroups';

const inputsStyle = {
  color: { color: '#4cafe5' },
};

export default function AutoAllocateForm({
  selectedDate,
  isNightMode,
  orgUnitId,
  orgUnitTeamId,
  clinicians,
  autoAllocateCallback,
  handleAutoUnallocateCancel,
  groupsToExclude,
  onClose,
}) {
  const dispatch = useDispatch();

  const [visitPriority, setVisitPriority] = useState('');
  const [timeOfDay, setTimeOfDay] = useState('');
  const [autoAllocationModes, setAutoAllocationModes] = useState([]);
  const [excludedCliniciansIds, setExcludedCliniciansIds] = useState([]);
  const [isBundlingEnabled, setIsBundlingEnabled] = useState(false);
  const [clinicianGroupsOnDay, setClinicianGroupsOnDay] = useState([]);
  const { data: clinicianGroupsData } = useFetchClinicianGroupsOnDay(false);

  useEffect(() => {
    if (clinicianGroupsData.mappedClinicianGroups)
      setClinicianGroupsOnDay(clinicianGroupsData.mappedClinicianGroups.filter(x => x.isValidOnDay));
  }, [clinicianGroupsData]);

  const isClinicianPartOfGroup = clinicianId => {
    if (clinicianGroupsOnDay.length > 0) {
      return clinicianGroupsOnDay.findIndex(x => x.clinicianIds.includes(clinicianId)) > -1;
    }
    return false;
  };

  const handleExcludedClinicians = (excludedClinicianId, event) => {
    if (event.target.checked) {
      if (!excludedCliniciansIds.includes(excludedClinicianId)) {
        setExcludedCliniciansIds([...excludedCliniciansIds, excludedClinicianId]);
      }
    } else {
      const currentState = excludedCliniciansIds.filter(x => x !== excludedClinicianId);
      setExcludedCliniciansIds(currentState);
    }
  };

  const includePhoneCallTreatments = event => {
    if (event.target.checked) {
      setAutoAllocationModes([...autoAllocationModes, AutoAllocationMode.phoneCalls]);
    } else {
      const currentState = autoAllocationModes.filter(x => x !== AutoAllocationMode.phoneCalls);
      setAutoAllocationModes(currentState);
    }
  };

  const includeBundling = event => {
    setIsBundlingEnabled(event.target.checked);
  };

  const includeClinicianGroups = event => {
    if (event.target.checked) {
      setAutoAllocationModes([...autoAllocationModes, AutoAllocationMode.clinicianGroups]);
    } else {
      const currentState = autoAllocationModes.filter(x => x !== AutoAllocationMode.clinicianGroups);
      setAutoAllocationModes(currentState);
    }
  };

  const canRun = () => {
    return visitPriority && timeOfDay;
  };

  const onNoneCheckboxChanged = () => {
    setExcludedCliniciansIds([]);
  };

  const runAutoAllocate = () => {
    dispatch(
      autoAllocate({
        date: selectedDate,
        orgUnitId,
        isNightMode,
        visitPriority: visitPriority.id,
        timeOfDay: TimeOfDay[timeOfDay],
        excludedCliniciansIds,
        createLog: true,
        autoAllocationModes,
        groupsToExclude,
        isBundlingEnabled,
        orgUnitTeamId,
      }),
    )
      .then(autoAllocateCallback)
      .catch(autoAllocateCallback);
    handleAutoUnallocateCancel();
  };

  const headerRender = () => {
    return (
      <div className="sidePanel-header form-header">
        <div className="sidePanel-header-subtitle">
          <Box display="flex">
            <Box flexGrow={1} py={1}>
              <Typography variant="subtitle1">AutoAllocate</Typography>
            </Box>
            <Box>
              <IconButton onClick={onClose}>
                <CancelIcon />
              </IconButton>
            </Box>
          </Box>
        </div>
      </div>
    );
  };

  const autoAllocateFormRender = () => {
    return (
      <div>
        <form className="sidePanel-content content-top-padding" noValidate autoComplete="off" disabled="disabled">
          <div className="sidePanel-form">
            <InputLabel style={inputsStyle.color} id="visitPriority">
              Visit Priority
            </InputLabel>
            <RagStatusPicker onChange={value => setVisitPriority(value)} selectedItem={visitPriority} />
            <InputLabel style={inputsStyle.color} id="timeOfDay">
              Time Of Day
            </InputLabel>
            <Select
              name="timeOfDay"
              labelId="timeOfDay"
              multiple={false}
              onChange={e => setTimeOfDay(e.target.value)}
              value={timeOfDay}
              autoWidth
            >
              {Object.keys(TimeOfDay).map(timeOfDayValue => (
                <MenuItem
                  key={timeOfDayValue}
                  disabled={(timeOfDayValue === 'Night' && !isNightMode) || (timeOfDayValue !== 'Night' && isNightMode)}
                  value={timeOfDayValue}
                >
                  {[timeOfDayValue]}
                </MenuItem>
              ))}
            </Select>
            <Box display={'flex'} flexDirection={'column'}>
              <FormControlLabel
                control={<Checkbox onClick={includePhoneCallTreatments} color="primary" />}
                label={'Include phone calls'}
              />
              <FormControlLabel
                control={<Checkbox onClick={includeBundling} color="primary" />}
                label={'Enable bundling of visits'}
              />
              <FormControlLabel
                control={<Checkbox onClick={includeClinicianGroups} color="primary" />}
                label={'Enable allocation of visits to Clinician Groups'}
              />
            </Box>
            <InputLabel style={inputsStyle.color} id="excludedClinicians">
              Excluded Clinicians
            </InputLabel>
            <List className="list-items">
              <ListItem button>
                <Checkbox
                  name="isChecked"
                  value={excludedCliniciansIds.length === 0}
                  checked={excludedCliniciansIds.length === 0}
                  onChange={onNoneCheckboxChanged}
                  color="primary"
                />
                <ListItemText secondary="None" />
              </ListItem>
              {clinicians &&
                clinicians
                  .filter(clinician => !isClinicianPartOfGroup(clinician.id))
                  .map(clinician => (
                    <ListItem button key={clinician.id} onChange={e => handleExcludedClinicians(clinician.id, e)}>
                      <Checkbox
                        color="primary"
                        checked={excludedCliniciansIds.includes(clinician.id)}
                        name={clinician.name}
                      />
                      <ListItemText secondary={clinician.name} />
                    </ListItem>
                  ))}
            </List>
          </div>
        </form>
      </div>
    );
  };

  const footerRender = () => {
    return (
      <div className="sidePanel-footer">
        {isBundlingEnabled ? (
          <div style={{ margin: '5%' }}>
            <AlertBox
              style={{ color: 'red' }}
              content={'The bundling of visits may result in some high priority visits not being allocated.'}
              type="notification"
            />
          </div>
        ) : (
          <React.Fragment />
        )}
        <div className="sidePanel-buttons">
          <div className="sidePanel-BackButton">
            <Button variant="contained" color="primary" onClick={onClose}>
              Cancel
            </Button>
          </div>
          <div className="sidePanel-saveButton">
            <Button disabled={!canRun()} variant="contained" color="secondary" onClick={runAutoAllocate}>
              Run
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="side-panel-content-wrapper">
      <SwipeableDrawer anchor="right" open onOpen={() => {}} onClose={() => {}}>
        <div className="sidePanel-wrapper" style={{ width: '450px' }}>
          {headerRender()}
          {autoAllocateFormRender()}
          {footerRender()}
        </div>
      </SwipeableDrawer>
    </section>
  );
}
